/**
 * Statement Builder QC sub-components.
 *
 * Five small panels that the Statement Builder editor mounts:
 *   <StatementStrengthPanel>  — 8-dimension audit grid replacing the "6/6 required" bar
 *   <ReviewerLensPanel>       — narrative summary (Strong / Missing / Could weaken)
 *   <VagueFlagInline>         — inline chip rendered under a single textarea
 *   <ToneRiskInline>          — inline warning chip for tone / credibility risks
 *   <LengthToggle>            — Short / Standard / Detailed pills + word-count guidance
 *   <GuardrailNote>           — credibility reminder rendered above the preview
 *   <EvidenceLinkButton>      — opens a small popover to link evidence to a prompt
 *
 * All visual styling is inline so the components are portable (no extra CSS
 * file to keep in sync) and obvious in the JSX. Apple-Sequoia tokens are
 * pulled via CSS vars (--surface, --border, --text-primary).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ShieldCheck, Warning, WarningCircle, Eye, Link as LinkIcon, X, FileText, ListChecks,
  Lightbulb, CheckCircle, ChartLine, Plus, Trash,
} from "@phosphor-icons/react";
import {
  STATUS_META, statusToneColor, LENGTH_TARGETS, lengthGuidance,
} from "@/lib/statementAudit";

// ---------------------------------------------------------------------------
// StatementStrengthPanel
// ---------------------------------------------------------------------------

export function StatementStrengthPanel({ dimensions }) {
  if (!dimensions?.length) return null;
  const strong = dimensions.filter((d) => d.status === "strong").length;
  return (
    <div
      className="panel"
      data-testid="stmt-strength-panel"
      style={{ borderRadius: 14, overflow: "hidden" }}
    >
      <div className="panel-header" style={{ alignItems: "center" }}>
        <ChartLine size={16} weight="bold" />
        <span>STATEMENT STRENGTH</span>
        <span className="header-count mono" data-testid="stmt-strength-count">
          {strong}/{dimensions.length} STRONG
        </span>
      </div>
      <div className="panel-body" style={{ padding: 14 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 8,
          }}
        >
          {dimensions.map((d) => {
            const meta = STATUS_META[d.status] || STATUS_META["needs-review"];
            const palette = statusToneColor(meta.tone);
            return (
              <div
                key={d.id}
                data-testid={`stmt-dim-${d.id}`}
                title={d.suggestion || d.reason}
                style={{
                  padding: "10px 12px",
                  borderRadius: 10,
                  background: palette.bg,
                  border: `1px solid ${palette.border}`,
                }}
              >
                <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-0.01em", marginBottom: 4 }}>
                  {d.label}
                </div>
                <div
                  className="mono"
                  data-testid={`stmt-dim-status-${d.id}`}
                  style={{
                    fontSize: 9.5,
                    fontWeight: 800,
                    letterSpacing: "0.08em",
                    color: palette.text,
                    textTransform: "uppercase",
                  }}
                >
                  {meta.label}
                </div>
                {d.reason && (
                  <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 4, lineHeight: 1.45 }}>
                    {d.reason}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReviewerLensPanel
// ---------------------------------------------------------------------------

export function ReviewerLensPanel({ lens }) {
  const [open, setOpen] = useState(true);
  if (!lens) return null;
  const { strong = [], missing = [], weakening = [] } = lens;
  const total = strong.length + missing.length + weakening.length;
  if (total === 0) return null;
  return (
    <div className="panel" data-testid="stmt-reviewer-lens" style={{ borderRadius: 14 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="stmt-reviewer-lens-toggle"
        className="panel-header"
        style={{ width: "100%", background: "none", border: "none", textAlign: "left", cursor: "pointer" }}
      >
        <Eye size={16} weight="bold" />
        <span>REVIEWER LENS</span>
        <span className="header-count mono">{open ? "HIDE" : "SHOW"}</span>
      </button>
      {open && (
        <div className="panel-body" style={{ padding: 14, display: "grid", gap: 14 }}>
          <LensBucket
            title="What's strong"
            icon={CheckCircle}
            tone="green"
            items={strong.map((label) => ({ label, suggestion: "" }))}
            testIdPrefix="stmt-lens-strong"
            emptyMsg="Once you fill prompts with concrete details, the strongest sections show up here."
          />
          <LensBucket
            title="What's missing"
            icon={WarningCircle}
            tone="red"
            items={missing}
            testIdPrefix="stmt-lens-missing"
            emptyMsg="Nothing critical missing — nice."
          />
          <LensBucket
            title="What could weaken it"
            icon={Warning}
            tone="amber"
            items={weakening}
            testIdPrefix="stmt-lens-weakening"
            emptyMsg="No weakeners flagged."
          />
        </div>
      )}
    </div>
  );
}

function LensBucket({ title, icon: Icon, tone, items, testIdPrefix, emptyMsg }) {
  const palette = statusToneColor(tone);
  return (
    <div
      data-testid={testIdPrefix}
      style={{
        padding: "12px 14px",
        borderRadius: 12,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Icon size={15} weight="fill" color={palette.text} />
        <span style={{ fontSize: 11, fontWeight: 800, color: palette.text, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          {title}
        </span>
        <span className="mono" style={{ marginLeft: "auto", fontSize: 10, color: palette.text, opacity: 0.7 }}>
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-secondary)", fontStyle: "italic" }}>{emptyMsg}</div>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 16, display: "grid", gap: 6 }}>
          {items.map((item, i) => (
            <li key={`${testIdPrefix}-${i}`} data-testid={`${testIdPrefix}-item-${i}`} style={{ fontSize: 12.5, color: "var(--text-primary)", lineHeight: 1.55 }}>
              <strong>{item.label}</strong>
              {item.suggestion && <span style={{ color: "var(--text-secondary)" }}> — {item.suggestion}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// VagueFlagInline + ToneRiskInline (rendered inside <PromptCard>)
// ---------------------------------------------------------------------------

export function VagueFlagInline({ flags, testId }) {
  if (!flags?.length) return null;
  return (
    <div
      data-testid={testId}
      style={{
        marginTop: 8,
        padding: "8px 10px",
        background: "rgba(217,119,6,0.06)",
        border: "1px solid rgba(217,119,6,0.25)",
        borderRadius: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <Lightbulb size={12} weight="bold" color="#b45309" />
        <span style={{ fontSize: 10, fontWeight: 800, color: "#b45309", letterSpacing: "0.08em" }}>
          SPECIFICITY — REPLACE WITH CONCRETE DETAIL
        </span>
      </div>
      <ul style={{ margin: 0, paddingLeft: 14, display: "grid", gap: 4 }}>
        {flags.map((f, i) => (
          <li key={`${testId}-${i}`} style={{ fontSize: 11.5, color: "var(--text-primary)", lineHeight: 1.5 }}>
            <span className="mono" style={{ background: "rgba(217,119,6,0.16)", padding: "1px 5px", borderRadius: 4 }}>
              {f.phrase}
            </span>
            <span style={{ color: "var(--text-secondary)" }}> — {f.nudge}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ToneRiskInline({ risks, testId }) {
  if (!risks?.length) return null;
  const warns = risks.filter((r) => r.severity === "warn");
  const infos = risks.filter((r) => r.severity === "info");
  if (warns.length === 0 && infos.length === 0) return null;
  return (
    <div
      data-testid={testId}
      style={{
        marginTop: 8,
        padding: "8px 10px",
        background: warns.length > 0 ? "rgba(220,38,38,0.06)" : "rgba(31,58,107,0.06)",
        border: `1px solid ${warns.length > 0 ? "rgba(220,38,38,0.25)" : "rgba(31,58,107,0.22)"}`,
        borderRadius: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <ShieldCheck size={12} weight="bold" color={warns.length > 0 ? "#b91c1c" : "#1F3A6B"} />
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: warns.length > 0 ? "#b91c1c" : "#1F3A6B" }}>
          TONE / CREDIBILITY CHECK
        </span>
      </div>
      <ul style={{ margin: 0, paddingLeft: 14, display: "grid", gap: 4 }}>
        {[...warns, ...infos].map((r, i) => (
          <li key={`${testId}-${i}`} style={{ fontSize: 11.5, color: "var(--text-primary)", lineHeight: 1.5 }}>
            <span className="mono" style={{ background: r.severity === "warn" ? "rgba(220,38,38,0.12)" : "rgba(31,58,107,0.10)", padding: "1px 5px", borderRadius: 4 }}>
              {r.snippet}
            </span>
            <span style={{ color: "var(--text-secondary)" }}> — {r.message}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LengthToggle — Short / Standard / Detailed pills + guidance
// ---------------------------------------------------------------------------

export function LengthToggle({ target, onChange, currentWordCount }) {
  const guidance = lengthGuidance(target, currentWordCount || 0);
  return (
    <div data-testid="stmt-length-toggle" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: "var(--text-secondary)" }}>
        TARGET LENGTH
      </span>
      <div style={{ display: "inline-flex", padding: 3, background: "var(--surface-2, #F1F5F9)", borderRadius: 999, border: "1px solid var(--border)" }}>
        {Object.entries(LENGTH_TARGETS).map(([key, t]) => {
          const active = target === key;
          return (
            <button
              type="button"
              key={key}
              onClick={() => onChange(key)}
              data-testid={`stmt-length-${key}`}
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: "5px 12px",
                borderRadius: 999,
                border: "none",
                background: active ? "var(--brand-accent, #1F3A6B)" : "transparent",
                color: active ? "#fff" : "var(--text-primary)",
                cursor: "pointer",
                letterSpacing: "-0.005em",
                transition: "background 180ms ease",
              }}
            >
              {t.label} <span className="mono" style={{ opacity: 0.6, fontWeight: 600 }}>{t.min}–{t.max}w</span>
            </button>
          );
        })}
      </div>
      {guidance && (
        <span
          data-testid="stmt-length-guidance"
          className="mono"
          style={{
            fontSize: 11,
            fontWeight: 700,
            padding: "3px 8px",
            borderRadius: 999,
            background: guidance.state === "in-range" ? "rgba(16,185,129,0.10)" : "rgba(217,119,6,0.10)",
            color: guidance.state === "in-range" ? "#047857" : "#b45309",
            border: `1px solid ${guidance.state === "in-range" ? "rgba(16,185,129,0.32)" : "rgba(217,119,6,0.32)"}`,
          }}
        >
          {currentWordCount}w · {guidance.label}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// GuardrailNote
// ---------------------------------------------------------------------------

export function GuardrailNote() {
  return (
    <div
      data-testid="stmt-guardrail-note"
      style={{
        padding: "10px 12px",
        marginBottom: 12,
        background: "rgba(31,58,107,0.05)",
        border: "1px solid rgba(31,58,107,0.18)",
        borderRadius: 10,
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
      }}
    >
      <ShieldCheck size={16} weight="duotone" color="#1F3A6B" style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ fontSize: 12, color: "var(--text-primary)", lineHeight: 1.55 }}>
        Only include symptoms, events, and impacts that are true to your experience.
        Do not exaggerate. Specific examples are stronger than dramatic wording.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EvidenceLinkButton — small popover, linkable to evidence + lay-witness statements
// ---------------------------------------------------------------------------

const KIND_META = {
  evidence:        { label: "Record",            icon: FileText },
  "lay-witness":   { label: "Buddy Statement",   icon: FileText },
  appointment:     { label: "Appointment",       icon: FileText },
  symptom:         { label: "Symptom Log",       icon: FileText },
  prescription:    { label: "Prescription",      icon: FileText },
};

export function EvidenceLinkButton({ promptId, linked = [], sources = [], onChange, testId }) {
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onClickOutside(e) {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  function toggleLink(item) {
    const exists = linked.find((l) => l.kind === item.kind && l.id === item.id);
    const next = exists
      ? linked.filter((l) => !(l.kind === item.kind && l.id === item.id))
      : [...linked, item];
    onChange(next);
  }

  function removeLink(item) {
    onChange(linked.filter((l) => !(l.kind === item.kind && l.id === item.id)));
  }

  const groupedSources = useMemo(() => {
    const groups = {};
    sources.forEach((s) => {
      if (!groups[s.kind]) groups[s.kind] = [];
      groups[s.kind].push(s);
    });
    return groups;
  }, [sources]);

  return (
    <div style={{ position: "relative", display: "inline-block" }} data-testid={`evidence-link-wrap-${promptId}`}>
      <button
        type="button"
        className="btn btn--outline btn--sm"
        onClick={() => setOpen((v) => !v)}
        data-testid={testId || `evidence-link-btn-${promptId}`}
        title="Link evidence to this section"
        style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
      >
        <LinkIcon size={12} weight="bold" />
        LINK EVIDENCE
        {linked.length > 0 && (
          <span className="mono" style={{ fontSize: 10, padding: "0 6px", background: "var(--brand-accent, #1F3A6B)", color: "#fff", borderRadius: 999 }}>
            {linked.length}
          </span>
        )}
      </button>

      {linked.length > 0 && (
        <div data-testid={`evidence-link-chips-${promptId}`} style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
          {linked.map((l) => {
            const meta = KIND_META[l.kind] || KIND_META.evidence;
            const Icon = meta.icon;
            return (
              <span
                key={`${l.kind}-${l.id}`}
                data-testid={`evidence-link-chip-${promptId}-${l.kind}-${l.id}`}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "2px 8px",
                  background: "var(--surface-2, #F1F5F9)",
                  border: "1px solid var(--border)",
                  borderRadius: 999,
                  fontSize: 10.5,
                  fontWeight: 600,
                  color: "var(--text-primary)",
                  maxWidth: 280,
                }}
              >
                <Icon size={10} weight="bold" />
                <span style={{ opacity: 0.7 }}>{meta.label}:</span>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.label}</span>
                <button
                  type="button"
                  onClick={() => removeLink(l)}
                  data-testid={`evidence-link-remove-${promptId}-${l.kind}-${l.id}`}
                  aria-label="Remove link"
                  style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, color: "var(--text-secondary)" }}
                >
                  <X size={10} weight="bold" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {open && (
        <div
          ref={popRef}
          data-testid={`evidence-link-pop-${promptId}`}
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            zIndex: 60,
            minWidth: 320,
            maxWidth: 420,
            maxHeight: 360,
            overflowY: "auto",
            background: "#fff",
            border: "1px solid var(--border)",
            borderRadius: 12,
            boxShadow: "0 18px 40px rgba(15,23,42,0.18)",
            padding: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.05em", color: "var(--text-primary)" }}>
              LINK EVIDENCE
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid={`evidence-link-close-${promptId}`}
              aria-label="Close"
              style={{ border: "none", background: "transparent", cursor: "pointer" }}
            >
              <X size={12} weight="bold" />
            </button>
          </div>
          {sources.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--text-secondary)", padding: "8px 4px", lineHeight: 1.5 }}>
              No items in your workspace yet. Add an Evidence record, a Buddy Statement (lay-witness), an Appointment, a Symptom log, or a Prescription, then come back to link it here.
            </div>
          ) : (
            Object.entries(groupedSources).map(([kind, items]) => {
              const meta = KIND_META[kind] || KIND_META.evidence;
              return (
                <div key={kind} style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: "var(--text-secondary)", marginBottom: 4 }}>
                    {meta.label.toUpperCase()}S
                  </div>
                  <div style={{ display: "grid", gap: 4 }}>
                    {items.map((s) => {
                      const isLinked = !!linked.find((l) => l.kind === s.kind && l.id === s.id);
                      return (
                        <button
                          key={`${s.kind}-${s.id}`}
                          type="button"
                          onClick={() => toggleLink(s)}
                          data-testid={`evidence-link-option-${promptId}-${s.kind}-${s.id}`}
                          style={{
                            textAlign: "left",
                            padding: "6px 8px",
                            borderRadius: 8,
                            border: `1px solid ${isLinked ? "rgba(16,185,129,0.40)" : "var(--border)"}`,
                            background: isLinked ? "rgba(16,185,129,0.08)" : "var(--surface)",
                            cursor: "pointer",
                            display: "flex",
                            gap: 6,
                            alignItems: "center",
                            fontSize: 12,
                          }}
                        >
                          {isLinked
                            ? <CheckCircle size={12} weight="fill" color="#047857" />
                            : <Plus size={12} weight="bold" color="var(--text-secondary)" />}
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{s.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SectionedPreview — renders the right-column statement with section headers
// ---------------------------------------------------------------------------

/**
 * @param {object[]} sections   [{ id, title, body }] in display order.
 *                              Sections with empty body are skipped.
 */
export function SectionedPreview({ sections }) {
  const nonEmpty = sections.filter((s) => (s.body || "").trim().length > 0);
  if (nonEmpty.length === 0) {
    return (
      <div className="stmt-preview-empty" data-testid="stmt-preview-empty" style={{ padding: 24 }}>
        <ListChecks size={24} weight="duotone" />
        <div style={{ fontSize: 13.5, lineHeight: 1.6, color: "var(--text-primary)", marginTop: 10 }}>
          Your statement will build here as you answer each section. Start with <strong>onset</strong>, then explain <strong>service connection</strong>, <strong>symptoms</strong>, <strong>continuity</strong>, <strong>current impact</strong>, and <strong>treatment</strong>.
        </div>
      </div>
    );
  }
  return (
    <div className="stmt-preview-sectioned" data-testid="stmt-preview-sectioned">
      {nonEmpty.map((s) => (
        <section
          key={s.id}
          data-testid={`stmt-preview-section-${s.id}`}
          style={{ marginBottom: 18 }}
        >
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 800,
              letterSpacing: "0.12em",
              color: "var(--brand-accent, #1F3A6B)",
              borderBottom: "1px solid var(--border)",
              paddingBottom: 4,
              marginBottom: 8,
              textTransform: "uppercase",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span>{s.title}</span>
            {s.aiApplied && (
              <span
                data-testid={`stmt-preview-ai-applied-${s.id}`}
                title="This section was applied from an AI draft action. Use Revert AI changes to restore your original answers."
                style={{
                  display: "inline-flex", alignItems: "center", gap: 3,
                  fontSize: 9, fontWeight: 700, letterSpacing: "0.06em",
                  padding: "1px 6px",
                  borderRadius: 999,
                  background: "rgba(31,58,107,0.10)",
                  border: "1px solid rgba(31,58,107,0.30)",
                  color: "#1F3A6B",
                }}
              >
                AI APPLIED
              </span>
            )}
          </div>
          <div style={{ fontSize: 13.5, lineHeight: 1.65, color: "var(--text-primary)", whiteSpace: "pre-wrap" }}>
            {s.body}
          </div>
        </section>
      ))}
    </div>
  );
}

// stub re-exports for unused imports (linter quiet)
export const __unusedIcons = { Trash };
